import React from 'react';
import { useTranslation } from 'react-i18next';
import { Sheet } from '@heroui-pro/react';
import type { ChartAccount, LocaleCode } from '@/api/models';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { AccountForm } from './AccountForm';

type AccountFormDrawerProps = {
    isOpen: boolean;
    /** The account being edited; `null` = create. */
    account: ChartAccount | null;
    /** The full chart — parent options + client code-uniqueness. */
    accounts: ChartAccount[];
    locale: LocaleCode;
    /**
     * Bumped each time the drawer is opened, so every open mounts a fresh form
     * (a second "Add account" doesn't inherit the previous draft).
     */
    instanceId: number;
    onClose: () => void;
    /** Fired after a successful create / update / archive. */
    onSaved: () => void;
};

/**
 * Right-side drawer hosting the {@link AccountForm} — sibling of the Journal
 * entry drawer (same HeroUI Pro `Sheet` chrome, vocabulary, and focus handling).
 * The chart stays visible behind it so the user keeps tree context. The Sheet
 * owns focus-trap/restore; the form owns all behaviour.
 */
export const AccountFormDrawer: React.FC<AccountFormDrawerProps> = ({
    isOpen,
    account,
    accounts,
    locale,
    instanceId,
    onClose,
    onSaved,
}) => {
    const { t } = useTranslation('pages');
    const isEdit = account !== null;

    return (
        <Sheet
            isOpen={isOpen}
            onOpenChange={(open) => !open && onClose()}
            placement="right"
        >
            <Sheet.Backdrop>
                <Sheet.Content className="w-full sm:max-w-[540px]">
                    <Sheet.Dialog
                        aria-label={t(
                            isEdit
                                ? 'chartOfAccounts.form.editTitle'
                                : 'chartOfAccounts.form.createTitle',
                        )}
                        {...getNodeTestIdProp('account-drawer')}
                    >
                        <Sheet.CloseTrigger />
                        <Sheet.Header>
                            <Sheet.Heading>
                                <span className="flex flex-col gap-1">
                                    <span className="text-lg font-semibold">
                                        {t(
                                            isEdit
                                                ? 'chartOfAccounts.form.editTitle'
                                                : 'chartOfAccounts.form.createTitle',
                                        )}
                                    </span>
                                    <span className="text-sm font-normal text-muted">
                                        {t(
                                            isEdit
                                                ? 'chartOfAccounts.form.editSubtitle'
                                                : 'chartOfAccounts.form.createSubtitle',
                                        )}
                                    </span>
                                </span>
                            </Sheet.Heading>
                        </Sheet.Header>

                        <Sheet.Body>
                            <AccountForm
                                account={account}
                                accounts={accounts}
                                key={`${account?.id ?? 'new'}-${instanceId}`}
                                locale={locale}
                                onCancel={onClose}
                                onSaved={onSaved}
                            />
                        </Sheet.Body>
                    </Sheet.Dialog>
                </Sheet.Content>
            </Sheet.Backdrop>
        </Sheet>
    );
};
